import Button from '@/components/Button';
import { HiOutlineExternalLink } from '@/components/react-icons/main';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import { actionModels } from '@shared/schema/actionModels';
import interpolateValue from '@shared/utils/interpolateValue';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { buildArgumentExternalRecordArguments } from './lib/buildArgumentExternalRecordArguments.js';
import { extractArgumentExternalRecordValue } from './lib/extractArgumentExternalRecordValue.js';

const ArgumentExternalRecordView = ({ argKey, dataSource, lookupActionId, overrideAction }) => {
  const activeActionSelector = useMemo(() => argumentSelectors[dataSource].activeAction({ lookupActionId, overrideAction }), [dataSource, lookupActionId, overrideAction]);
  const activeArgumentsSelector = useMemo(() => argumentSelectors[dataSource].activeArguments({ lookupActionId, overrideAction }), [dataSource, lookupActionId, overrideAction]);

  const appName = useSelector((state) => argumentSelectors[dataSource].activeApp(state)?.name || 'External System');
  const action = useArgSelector(activeActionSelector, lookupActionId);
  const credentials = useSelector(argumentSelectors[dataSource].activeCredentials);
  const activeArguments = useSelector((state) => activeArgumentsSelector(state, lookupActionId));

  const actionModel = action?.model;
  const actionGroupName = action?.groupName;
  const actionEntityUrl = action?.presentation?.entityUrl;
  const actionModelListUrl = action?.presentation?.modelListUrl;
  const actionOperation = action?.operation;

  const allArguments = useMemo(() => buildArgumentExternalRecordArguments(activeArguments), [activeArguments]);
  const argumentValue = useMemo(() => extractArgumentExternalRecordValue(activeArguments?.[argKey]), [activeArguments, argKey]);

  if (actionOperation !== 'update' || (!actionEntityUrl && !actionModelListUrl)) {
    return null;
  }

  const modelName = actionModels[actionModel]?.nickname || actionGroupName || 'Record';
  const hasValue = argumentValue && argumentValue !== '' && argumentValue !== null && argumentValue !== undefined;

  const interpolationContext = {
    arguments: allArguments,
    credentials: credentials?.secrets,
    input: { id: argumentValue?.value || argumentValue }
  };

  const interpolatedEntityUrl = actionEntityUrl ? interpolateValue(actionEntityUrl, interpolationContext) : null;
  const interpolatedModelListUrl = actionModelListUrl ? interpolateValue(actionModelListUrl, interpolationContext) : null;

  const viewUrl = hasValue && interpolatedEntityUrl ? interpolatedEntityUrl : interpolatedModelListUrl;

  if (!viewUrl) {
    return null;
  }

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Button //
      className={'external-record-view info text'}
      size={'xs'}
      icon={HiOutlineExternalLink}
      onClick={handleClick}
      title={`View ${hasValue ? 'this' : 'all'} ${modelName.toLowerCase()}${hasValue ? '' : 's'} in ${appName}`}
    />
  );
};

export default ArgumentExternalRecordView;
